import {
  ANALYTICS,
  CUSTOMERS,
  DASHBOARD,
  FEEDBACK,
  INBOX,
  GIFTS,
  LIMITS,
  PASSKEYS,
  PROFIT,
  RECONCILIATION,
  RISK_CENTER,
  SCHEDULES,
  SESSIONS,
  TRANSACTIONS,
} from '../../routes/routeConstants';

export interface PageGuide {
  // One plain sentence: what this page is for. Shown under the title, in the
  // sidebar tooltip and in the command menu.
  summary: string;
  // "How this page works": short, practical tips, shown once then on request.
  tips: string[];
}

// Keyed by the route's first path segment, so PageHeader finds its own guide.
export const PAGE_GUIDES: Record<string, PageGuide> = {
  [INBOX]: {
    summary: 'Everything that needs a decision from you, most urgent first.',
    tips: [
      'Items come from every page: risk alerts, purchases we couldn’t confirm, failed gift refunds, unhappy customers, the VTpass wallet and the system itself.',
      'Deal with an item on its own page and it leaves the Inbox by itself. Nothing to tick off.',
      'Press j and k to move between items and Enter to open one. It refreshes every minute.',
    ],
  },
  [CUSTOMERS]: {
    summary: 'Everything about one customer: how they pay, what they bought, gifts, schedules and risk.',
    tips: [
      'Open any customer by typing their phone number into ⌘K.',
      'Click a purchase, gift or schedule to see its full detail.',
      'Linked accounts share a card, email or recipient with this customer. Open them to compare.',
      'Chat allowance shows how much they’ve chatted today. Give them their own daily allowance, or clear today’s usage so the assistant answers again. Each needs a reason.',
    ],
  },
  [DASHBOARD]: {
    summary: 'How Hethera is doing at a glance, and anything that needs you now.',
    tips: [
      '“Needs attention” lists only things you should act on. When it says All clear, there’s nothing to do.',
      'Switch the big chart between Value, Revenue and Transactions. The date range at the top changes every number on the page.',
      'Hover over any ⓘ to see exactly what a number means. Your to-do list lives in the Inbox.',
    ],
  },
  [TRANSACTIONS]: {
    summary: 'Every payment Hethera has handled: bills, Dash gifts, Ejor and scheduled runs.',
    tips: [
      'Click a row to see its full story: status history, money movements and receipts.',
      'Set “Who paid” to Dash gifts or Ejor to see only those. A gift appears twice: the sender’s payment, then the delivery.',
      'Search works with a reference, phone number, provider reference, or a gift (DS-…) or Ejor code.',
      'Pending or Unknown means we’re still confirming with the bank or VTpass. Reconciliation settles these on its own.',
    ],
  },
  [SCHEDULES]: {
    summary: 'Bills customers set to pay automatically: once, weekly, monthly or before expiry.',
    tips: [
      'Click a schedule to see its runs and to pause, skip or cancel it.',
      'Only the customer can resume a paused schedule, with their PIN.',
      'A failed run is never retried. The customer can choose Pay now instead.',
      'From a run you can export its dispute record for Paystack.',
    ],
  },
  [GIFTS]: {
    summary: 'Airtime and electricity customers have bought for someone else (Dash).',
    tips: [
      'Click a gift to see where it is and to freeze, unfreeze or revoke it. Each action needs a short note.',
      'Every gift also shows in Transactions twice: the sender’s card payment and the delivery.',
      'Refunds always go back in full to the sender’s card.',
      'Search by gift code (DS-…) or a phone number.',
    ],
  },
  [LIMITS]: {
    summary: 'How much a customer can spend per purchase, per day and per month.',
    tips: [
      'Change the defaults for everyone, or find one customer by WhatsApp number to give them their own limit.',
      'A customer’s own limit needs a reason and can expire.',
      'While limits are switched off nothing is refused; the numbers apply once they’re on.',
    ],
  },
  [RISK_CENTER]: {
    summary: 'Automatic fraud and integrity checks, run every 5 minutes.',
    tips: [
      'Start with Critical, then High alerts.',
      'Acknowledge an alert while you look into it. Resolving or dismissing it needs a note.',
      'Click an alert to see the customer and the transactions behind it.',
      'Rule settings let you tune thresholds or switch a rule off.',
    ],
  },
  [RECONCILIATION]: {
    summary: 'Makes sure our records match Paystack and VTpass, and settles purchases still being confirmed.',
    tips: [
      'Purchases we couldn’t confirm are re-checked automatically: often at first, then less often.',
      '“Run reconciliation now” checks everything at once. “Reconcile now” on a transaction checks just that one.',
      'A card charge confirmed late is refunded. It is never used to buy late.',
    ],
  },
  [PROFIT]: {
    summary: 'What Hethera keeps after Paystack, VTpass, Claude and WhatsApp costs.',
    tips: [
      'Profit is what customers paid in bills and fees, minus Paystack fees, the VTpass cost, and Claude and WhatsApp costs.',
      'Loss days and loss-making customers are highlighted so you can spot them quickly.',
      'Unit economics shows what one purchase earns at each amount at today’s pricing, and where a product still loses money.',
      'Settings → Pricing sets the airtime cap, the cable and electricity fees, the data loss guard and Paystack’s fee. Changes apply from the next purchase and show in Unit economics straight away.',
      'Settings also holds exchange rates, chat prices and VTpass commission rates.',
      'Settings → Chat model picks the Claude model that answers customers. Only models with a Claude price can be chosen; it applies from the next message.',
      'Settings → Chat limits sets how much every customer can chat in a day. One customer’s own allowance is set on their customer page.',
    ],
  },
  [SESSIONS]: {
    summary: 'Each WhatsApp conversation, with what it cost and what it earned.',
    tips: [
      'A session starts with a customer’s message and ends when the chat goes quiet.',
      'Click a session to see its costs and any purchase made in it.',
    ],
  },
  [ANALYTICS]: {
    summary: 'How Hethera is growing: new customers and purchases over time.',
    tips: [
      'Pick a year at the top, then a month to see its days.',
      'Registered = messaged us. Onboarded = finished setting up and can pay.',
    ],
  },
  [FEEDBACK]: {
    summary: 'What customers told us after a purchase.',
    tips: [
      'Click an entry to read it, change its status and add an internal note.',
      'Card numbers in comments are masked automatically.',
    ],
  },
  [PASSKEYS]: {
    summary: 'The passkeys and recovery codes that protect your admin sign-in.',
    tips: [
      'Add a passkey on each device you sign in from.',
      'Each recovery code works once. Keep them somewhere safe, away from this computer.',
      'Removing a passkey or renewing codes may ask you to confirm with a passkey first.',
    ],
  },
};

export const guideForPath = (pathname: string): PageGuide | undefined =>
  PAGE_GUIDES[pathname.split('/').filter(Boolean)[0] ?? DASHBOARD];
